import { z } from 'zod';
import { emailSchema } from './schemas';

/** Schemas da Fase 4. O cliente informa O QUE quer pagar; o valor é sempre calculado pelo servidor. */

const cents = z.number().int('Valor inválido').min(1, 'Valor inválido').max(100_000_00, 'Valor alto demais');
const cpf = z
  .string()
  .trim()
  .transform((v) => v.replace(/\D/g, ''))
  .pipe(z.string().regex(/^\d{11}$/, 'CPF deve ter 11 dígitos'));

export const PAYMENT_MODES = ['TOTAL', 'ITEMS', 'AMOUNT'] as const;

export const itemUnitsSchema = z.array(z.object({ id: z.uuid(), units: z.number().int().min(1).max(999) })).max(200);

const selection = {
  mode: z.enum(PAYMENT_MODES),
  /** Itens pagos por inteiro (todas as unidades que faltam). */
  itemIds: z.array(z.uuid()).max(200).optional(),
  /** Só parte das unidades de um item (ex.: 1 de 3 cervejas); o resto continua na conta. */
  itemUnits: itemUnitsSchema.optional(),
  amountCents: cents.optional(),
};
function refineSelection<T extends { mode: string; itemIds?: string[]; itemUnits?: { id: string; units: number }[]; amountCents?: number }>(s: z.ZodType<T>) {
  return s
    .refine((v) => v.mode !== 'ITEMS' || (v.itemIds?.length ?? 0) + (v.itemUnits?.length ?? 0) > 0, { message: 'Escolha pelo menos um item', path: ['itemIds'] })
    .refine((v) => v.mode !== 'AMOUNT' || v.amountCents !== undefined, { message: 'Informe o valor', path: ['amountCents'] });
}

export const createPaymentSchema = refineSelection(
  z.object({
    idempotencyKey: z.uuid(),
    method: z.enum(['PIX', 'CARD']),
    ...selection,
    /** Exigido pelo Mercado Pago para Pix; não é guardado pelo Garlic. */
    payerEmail: emailSchema.optional(),
    payerCpf: cpf.optional().or(z.literal('').transform(() => undefined)),
  }),
).refine((v) => v.method !== 'PIX' || !!v.payerEmail, { message: 'Informe seu e-mail para gerar o Pix', path: ['payerEmail'] });

/**
 * O que o navegador manda para pagar com cartão: SÓ o token de uso único gerado pelo formulário do Mercado Pago.
 * `strict`: um corpo com número, CVV, validade ou qualquer campo a mais é RECUSADO (nunca chega a ser usado).
 */
export const cardPaymentSchema = z
  .object({
    token: z
      .string()
      .min(8)
      .max(200)
      .regex(/^[A-Za-z0-9_-]+$/, 'Token inválido')
      .refine((t) => !/^\d{12,19}$/.test(t), 'Envie o token do Mercado Pago, nunca o número do cartão'),
    paymentMethodId: z.string().min(2).max(40),
    paymentType: z.enum(['credit_card', 'debit_card']),
    installments: z.number().int().min(1).max(12),
    payerEmail: emailSchema,
    identification: z.object({ type: z.string().max(10), number: z.string().max(20) }).strict().optional(),
    deviceId: z.string().max(200).optional(),
  })
  .strict();

export const MANUAL_METHODS = ['CASH', 'CARD_TERMINAL', 'PIX_MANUAL', 'OTHER'] as const;
export const manualPaymentSchema = refineSelection(
  z.object({
    method: z.enum(MANUAL_METHODS),
    ...selection,
    note: z.string().trim().max(300).optional(),
  }),
);

export const discountSchema = z.object({
  amountCents: cents,
  reason: z.string().trim().min(5, 'Descreva o motivo (mínimo 5 caracteres)').max(300),
});

export const reversePaymentSchema = z.object({ reason: z.string().trim().min(5, 'Descreva o motivo').max(300) });

export const PAYMENT_METHOD_LABEL: Record<string, string> = {
  PIX: 'Pix (Mercado Pago)',
  CARD: 'Cartão (Mercado Pago)',
  CASH: 'Dinheiro',
  CARD_TERMINAL: 'Cartão na maquininha',
  PIX_MANUAL: 'Pix direto (registrado manualmente)',
  OTHER: 'Outro',
};

export const ATTEMPT_STATUS_LABEL: Record<string, string> = {
  CREATED: 'Aguardando dados do cartão',
  PENDING: 'Em processamento',
  APPROVED: 'Aprovado',
  REJECTED: 'Recusado',
  CANCELLED: 'Cancelado',
  EXPIRED: 'Expirado',
  REFUNDED: 'Estornado',
};
